import React from 'react';
import { PiFiles, PiUploadSimple } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';

interface MediaUploadDropzoneProps {
  /** Whether upload controls are enabled. */
  editable: boolean;
  /** Whether the dropzone actions are disabled. */
  disabled: boolean;
  /** Whether uploads are currently in flight. */
  loading: boolean;
  /** Whether files are currently hovering over the dropzone. */
  dragging: boolean;
  /** Localized count label for the current media collection. */
  countLabel: string;
  /** Dropzone title. */
  title: string;
  /** Helper text shown while dragging files over the dropzone. */
  helper: string;
  /** Upload action label. */
  actionLabel: string;
  /** Empty-state helper text shown when not dragging. */
  emptyLabel: string;
  /** Called when the browse action is clicked. */
  onBrowse: () => void;
  /** Drag-enter handler. */
  onDragEnter: React.DragEventHandler<HTMLDivElement>;
  /** Drag-leave handler. */
  onDragLeave: React.DragEventHandler<HTMLDivElement>;
  /** Drag-over handler. */
  onDragOver: React.DragEventHandler<HTMLDivElement>;
  /** Drop handler. */
  onDrop: React.DragEventHandler<HTMLDivElement>;
}

/**
 * Shared drag-and-drop upload surface for property media collections.
 */
export const MediaUploadDropzone: React.FC<MediaUploadDropzoneProps> = ({
  editable,
  disabled,
  loading,
  dragging,
  countLabel,
  title,
  helper,
  actionLabel,
  emptyLabel,
  onBrowse,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}) => (
  <div
    className={`rounded-2xl border border-dashed p-5 transition-colors ${
      dragging
        ? 'border-primary-400 bg-primary-50'
        : 'border-slate-300 bg-slate-50/80'
    }`}
    onDragEnter={onDragEnter}
    onDragLeave={onDragLeave}
    onDragOver={onDragOver}
    onDrop={onDrop}
  >
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-primary-100 p-3 text-primary-700">
          {dragging ? (
            <PiUploadSimple className="h-5 w-5" />
          ) : (
            <PiFiles className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0">
          <Text size="small" weight="semibold" className="text-slate-900">
            {title}
          </Text>
          <Text size="tiny" className="mt-1 text-slate-500">
            {dragging ? helper : emptyLabel}
          </Text>
          <div className="mt-3">
            <StatusPill variant="secondary" label={countLabel} />
          </div>
        </div>
      </div>

      {editable ? (
        <Button
          type="button"
          variant="neutral"
          appearance="outline"
          onClick={onBrowse}
          loading={loading}
          disabled={disabled}
          className="whitespace-nowrap"
        >
          {actionLabel}
        </Button>
      ) : null}
    </div>
  </div>
);
